import { IconEye } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef, FilterFn } from "@tanstack/react-table";
import { Grid, Input, Select, Typography } from "antd";
import { useMemo, useState } from "react";
import { useRouteOrgSlug } from "@/common/artemis-operations/organization";
import type { ArtemisJob } from "@/common/model";
import { JobStatus } from "@/common/model";
import { describeCron } from "@/common/utils";
import { CustomIcon, DateField } from "../base";
import {
	ResponsiveTable,
	type ResponsiveTableProps,
} from "../responsive-table";
import { TableFilterBar } from "../table-filter-bar";
import { JobStatusBadge, JobStatusLabelMap } from "./job-badges";
import { JobDetailsDrawer } from "./job-details";

const searchFilter: FilterFn<ArtemisJob> = (row, _columnId, value: string) => {
	const v = value.toLowerCase();
	return (
		row.original.name.toLowerCase().includes(v) ||
		(row.original.id as string).toLowerCase().includes(v)
	);
};

interface JobTableProps
	extends Omit<ResponsiveTableProps<ArtemisJob>, "columns"> {
	onRefresh?: () => void;
	workspaceId?: string;
	// Controlled server-side search/filter (owned by the route via usePagedList).
	search?: string;
	onSearchChange?: (v: string) => void;
	status?: string;
	onStatusChange?: (v: string | undefined) => void;
}

export function JobTable({
	onRefresh,
	workspaceId,
	loading,
	search,
	onSearchChange,
	status,
	onStatusChange,
	...props
}: JobTableProps) {
	const [selectedJob, setSelectedJob] = useState<ArtemisJob | null>(null);
	const navigate = useNavigate();
	const orgSlug = useRouteOrgSlug();
	const screens = Grid.useBreakpoint();
	const isCompact = !screens.lg;

	const columns: ColumnDef<ArtemisJob>[] = useMemo(
		() => [
			{
				accessorKey: "name",
				header: "Name",
				filterFn: searchFilter,
				cell: ({ row }) => (
					<Typography.Text ellipsis strong>
						{row.original.name}
					</Typography.Text>
				),
			},
			{
				accessorKey: "status",
				header: "Status",
				size: 110,
				filterFn: "equals",
				cell: ({ row }) => <JobStatusBadge status={row.original.status} />,
			},
			{
				accessorKey: "schedule",
				header: "Schedule",
				size: 220,
				cell: ({ row }) => (
					<Typography.Text ellipsis>
						{describeCron(row.original.schedule as string)}
					</Typography.Text>
				),
			},
			{
				accessorKey: "lastFiredAt",
				header: "Last run",
				size: 180,
				cell: ({ row }) =>
					row.original.lastFiredAt ? (
						<DateField value={row.original.lastFiredAt as string} />
					) : (
						<Typography.Text type="secondary">Never</Typography.Text>
					),
			},
			{
				accessorKey: "createdAt",
				header: "Created At",
				size: 180,
				cell: ({ row }) => (
					<DateField value={row.original.createdAt as string} />
				),
			},
			// {
			// 	accessorKey: "id",
			// 	header: "ID",
			// 	size: 180,
			// 	filterFn: searchFilter,
			// 	cell: ({ row }) => <IdField id={row.original.id as string} />,
			// },
		],
		[],
	);

	return (
		<>
			<ResponsiveTable<ArtemisJob>
				loading={loading}
				columns={columns}
				initialSorting={[{ id: "createdAt", desc: true }]}
				onRowClick={(job) => setSelectedJob(job)}
				rowActions={[
					{
						label: "View",
						icon: <CustomIcon icon={IconEye} />,
						onClick: (job) => setSelectedJob(job),
					},
				]}
				rowHref={(job) =>
					`/${orgSlug}/workspaces/${job.workspaceId}/jobs/${job.id}`
				}
				compactRender={(job) => (
					<article
						onKeyDown={() =>
							navigate({
								to: `/${orgSlug}/workspaces/${job.workspaceId}/jobs/${job.id}` as any,
							})
						}
						onClick={() =>
							navigate({
								to: `/${orgSlug}/workspaces/${job.workspaceId}/jobs/${job.id}` as any,
							})
						}
						style={{
							display: "flex",
							justifyContent: "space-between",
							width: "100%",
						}}
					>
						<Typography.Text strong ellipsis>
							{job.name}
						</Typography.Text>
						<JobStatusBadge status={job.status} />
					</article>
				)}
				filterBar={() => (
					<TableFilterBar isCompact={isCompact}>
						<Input.Search
							placeholder="Search by name or ID"
							allowClear
							style={{ width: isCompact ? "100%" : 220 }}
							value={search ?? ""}
							onChange={(e) => onSearchChange?.(e.target.value)}
							onSearch={(v) => onSearchChange?.(v)}
						/>
						<Select
							allowClear
							placeholder="Status"
							style={{ width: isCompact ? "100%" : 140 }}
							value={status ?? null}
							onChange={(v) => onStatusChange?.(v ?? undefined)}
							options={Object.values(JobStatus).map((s) => ({
								value: s,
								label: JobStatusLabelMap[s],
							}))}
						/>
					</TableFilterBar>
				)}
				onRefresh={onRefresh}
				{...props}
			/>

			<JobDetailsDrawer
				open={!!selectedJob}
				job={selectedJob ?? undefined}
				workspaceId={workspaceId}
				onClose={() => setSelectedJob(null)}
			/>
		</>
	);
}
